/**
 * OH360 Form Guard. Minimal submit-time bot blocker.
 *
 * v2.7 (2026-09-22): phishing-pretext rule on message fields (see looksLikePhishingPretext) and a
 *   visible notice under a blocked form (a silent block is a support ticket nobody can see).
 * v2.6 (2026-09-21): hash-name rule requires a digit (see looksLikeHashName).
 * v2.5 (2026-04-20). MINIMAL. Previous versions (v2.2-v2.4) added global
 * input-event handlers, periodic DOM scanning loops, fetch/XHR/sendBeacon
 * interception with content scanning, and an HTMLImageElement.prototype.src
 * override. Those paths suppressed Shopify trekkie analytics beacons
 * (monorail-edge.shopifysvc.com) and added measurable page lag on Shopify
 * product pages with many forms and high-frequency user input.
 *
 * v2.5 strips everything down to the single path that actually matters:
 * submit-time inspection of form values. If a form is being submitted
 * with a dot-obfuscated email or hash-name pattern in its fields, we
 * block the submission. That's it. No intervals, no network hooks, no
 * DOM scanning, no prototype overrides.
 *
 * Hosted at https://onehub360.com/form-guard/oh360-form-guard.js
 */
(function () {
  'use strict';
  if (window.__oh360FormGuardLoaded) return;
  window.__oh360FormGuardLoaded = true;
  window.__oh360FormGuardVersion = '2.5';

  try {

  // ── Detection rules ─────────────────────────────────────────────────────
  function looksLikeDotObfuscated(s) {
    if (!s) return false;
    var trimmed = String(s).trim().toLowerCase();
    var hasAt = trimmed.indexOf('@') !== -1;
    var local = hasAt ? trimmed.split('@')[0] : trimmed;
    var domain = hasAt ? (trimmed.split('@')[1] || '') : '';
    if (local.indexOf('.') === -1) return false;
    var dotCount = (local.match(/\./g) || []).length;
    if (dotCount >= 5 && !domain.endsWith('.mil')) return true;
    var segs = local.split('.').filter(Boolean);
    if (segs.length < 4) return false;
    var shortSegs = 0;
    for (var i = 0; i < segs.length; i++) if (segs[i].length <= 2) shortSegs++;
    return shortSegs >= Math.ceil(segs.length / 2);
  }

  function looksLikeHashName(name) {
    if (!name) return false;
    if (looksLikeDotObfuscated(name)) return true;
    var parts = String(name).trim().split(/\s+/);
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      // v2.6 (2026-09-21): a hash-looking name must carry a DIGIT. The v2.5
      // rule (/[0-9A-Z]/ plus /[a-z]/) matched every capitalised surname of
      // nine or more letters (Fleurbaaij, Cuthbertson, Christopher) and
      // silently cancelled those customers' contact form submits.
      if (part.length > 8 &&
          /^[A-Za-z0-9_\-]+$/.test(part) &&
          /[0-9]/.test(part) &&
          /[A-Za-z]/.test(part)) {
        return true;
      }
    }
    return false;
  }

  // v2.7 (2026-09-22): the "secured project PDF" phishing template that hit the contact form on
  // 2026-09-22 (three vague projects, "best email to securely send the PDF", then "click Continue to
  // download"). Message-like fields only. Phrases are lowercase substrings; add new templates here.
  var PHISHING_PHRASES = [
    'secured file', 'secure file', 'password-protected file', 'password protected file',
    'select continue', 'click continue', 'click on it, then select', 'continue to download',
    'compiled a detailed pdf', 'best email address to securely send', 'send over the property pdf',
    'property measurements, layout specifics', 'securely send over the', 'download the file to review',
    'view the document here', 'access the file via', 'the file will expire', 'shared a file with you'
  ];
  function looksLikePhishingPretext(s) {
    if (!s) return false;
    var t = String(s).toLowerCase().replace(/\s+/g, ' ');
    for (var i = 0; i < PHISHING_PHRASES.length; i++) {
      if (t.indexOf(PHISHING_PHRASES[i]) !== -1) return true;
    }
    // Two or more "project" pretexts plus a promised PDF and no digits at all (no size, no address).
    var projects = (t.match(/residential home|recording studio|office building|church|school|warehouse/g) || []).length;
    if (projects >= 2 && /\bpdf\b/.test(t) && !/[0-9]/.test(t)) return true;
    return false;
  }

  function looksBad(value, fieldKey) {
    if (!value) return null;
    var val = String(value).trim();
    if (!val) return null;
    var k = String(fieldKey || '').toLowerCase();
    var isEmailField = /email|e-mail/.test(k) || /@/.test(val);
    if (isEmailField && looksLikeDotObfuscated(val)) return 'dot_obfuscated_email';
    var isNameField = /first.?name|last.?name|full.?name|(^|[^a-z])name([^a-z]|$)/.test(k);
    if (isNameField && looksLikeHashName(val)) return 'hash_name';
    var isCompanyField = /company|organization|organisation/.test(k);
    if (isCompanyField && looksLikeHashName(val)) return 'hash_company';
    var isMessageField = /message|comment|body|custom|inquiry|details|textarea/.test(k) || val.length > 160;
    if (isMessageField && looksLikePhishingPretext(val)) return 'phishing_pretext';
    return null;
  }

  // ── Beacon (fire-and-forget diagnostic) ─────────────────────────────────
  function beacon(reasons, extra) {
    try {
      if (!navigator.sendBeacon) return;
      var payload = JSON.stringify({
        source: 'oh360-form-guard',
        host: location.host,
        path: location.pathname,
        reasons: reasons,
        extra: extra || {},
        ts: Date.now(),
      });
      navigator.sendBeacon('https://onehub360.com/api/tracking/spam-beacon', payload);
    } catch (_) {}
  }

  // ── The ONE hook: submit-time inspection ────────────────────────────────
  // Capture phase so we run before bubble-phase listeners (incl. tracker
  // change-capture listeners). If any field holds a hash-name or
  // dot-obfuscated email, block the submission.
  function onSubmit(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var reasons = [];
    var fields = form.querySelectorAll('input, textarea');
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (!f.value) continue;
      var r = looksBad(f.value, (f.name || '') + '|' + (f.id || '') + '|' + (f.tagName === 'TEXTAREA' ? 'textarea' : ''));
      if (r) reasons.push(r);
    }
    if (!reasons.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();
    try { if (window.console) console.warn('[oh360-form-guard] blocked submit:', reasons.join(' | ')); } catch (_) {}
    beacon(reasons, { formId: form.id || '', formAction: form.action || '' });
    showNotice(form);
  }

  // v2.7: a blocked form says so. Plain sentence, no jargon, the phone the site already publishes.
  function showNotice(form) {
    try {
      var n = form.querySelector('.oh360-form-guard-notice');
      if (!n) {
        n = document.createElement('p');
        n.className = 'oh360-form-guard-notice';
        n.setAttribute('role', 'alert');
        n.style.cssText = 'margin:12px 0 0;padding:10px 12px;border:1px solid #b00020;border-radius:6px;color:#b00020;font-size:15px;line-height:1.4;';
        form.appendChild(n);
      }
      n.textContent = 'This message could not be sent. Please call (888) 923-5777 and the team will help right away.';
      if (n.scrollIntoView) n.scrollIntoView({ block: 'nearest' });
    } catch (_) {}
  }
  document.addEventListener('submit', onSubmit, true);

  } catch (e) {
    // Top-level safety net. Form-guard must never throw into the host page.
    try { if (window.console) console.warn('[oh360-form-guard] init failed:', e && e.message); } catch (_) {}
  }
})();
